import React, { useMemo } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import {
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
} from "reactstrap";

import { CatalogCategory } from "../../../types/catalogCategory";
import { entityFormKey } from "../../../helpers/entityEditForm";

export interface CategoryFormValues {
  name: string;
  sort_order: number;
  is_active: boolean;
}

interface CategoryFormModalProps {
  isOpen: boolean;
  editingCategory: CatalogCategory | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (values: CategoryFormValues) => Promise<void>;
}

const emptyFormValues = (): CategoryFormValues => ({
  name: "",
  sort_order: 0,
  is_active: true,
});

const mapCategoryToFormValues = (category: CatalogCategory): CategoryFormValues => ({
  name: category.name,
  sort_order: category.sort_order,
  is_active: category.is_active,
});

const CategoryFormModal = ({
  isOpen,
  editingCategory,
  submitting,
  onClose,
  onSubmit,
}: CategoryFormModalProps) => {
  const { t } = useTranslation();

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string()
          .trim()
          .required(t("settings.catalog_categories.validation.nameRequired")),
        sort_order: Yup.number()
          .integer(t("settings.catalog_categories.validation.sortOrderInteger"))
          .min(0, t("settings.catalog_categories.validation.sortOrderMin"))
          .required(t("settings.catalog_categories.validation.sortOrderRequired")),
        is_active: Yup.boolean(),
      }),
    [t]
  );

  const formik = useFormik<CategoryFormValues>({
    enableReinitialize: true,
    initialValues: editingCategory
      ? mapCategoryToFormValues(editingCategory)
      : emptyFormValues(),
    validationSchema,
    onSubmit: async (values, helpers) => {
      await onSubmit(values);
      helpers.setSubmitting(false);
    },
  });

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>
        {editingCategory
          ? t("settings.catalog_categories.form.editTitle")
          : t("settings.catalog_categories.form.createTitle")}
      </ModalHeader>
      <ModalBody>
        <Form
          key={entityFormKey(editingCategory?.id)}
          onSubmit={(event) => {
            event.preventDefault();
            formik.handleSubmit();
          }}
        >
          <div className="mb-3">
            <Label htmlFor="category-name">
              {t("settings.catalog_categories.fields.name.label")}
            </Label>
            <Input
              id="category-name"
              name="name"
              placeholder={t("settings.catalog_categories.fields.name.placeholder")}
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.name && formik.errors.name)}
            />
            {formik.touched.name && formik.errors.name ? (
              <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
            ) : null}
          </div>

          <div className="mb-3">
            <Label htmlFor="category-sort-order">
              {t("settings.catalog_categories.fields.sortOrder.label")}
            </Label>
            <Input
              id="category-sort-order"
              name="sort_order"
              type="number"
              min={0}
              value={formik.values.sort_order}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.sort_order && formik.errors.sort_order)}
            />
            <div className="form-text">
              {t("settings.catalog_categories.fields.sortOrder.help")}
            </div>
            {formik.touched.sort_order && formik.errors.sort_order ? (
              <FormFeedback type="invalid">{formik.errors.sort_order}</FormFeedback>
            ) : null}
          </div>

          <div className="form-check form-switch mb-4">
            <Input
              id="category-is-active"
              name="is_active"
              type="checkbox"
              role="switch"
              className="form-check-input"
              checked={formik.values.is_active}
              onChange={formik.handleChange}
            />
            <Label className="form-check-label" htmlFor="category-is-active">
              {t("settings.catalog_categories.fields.isActive")}
            </Label>
          </div>

          <div className="d-flex gap-2 justify-content-end">
            <Button color="light" type="button" onClick={handleClose}>
              {t("settings.catalog_categories.actions.cancel")}
            </Button>
            <Button color="primary" type="submit" disabled={submitting || formik.isSubmitting}>
              {submitting || formik.isSubmitting ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <i className="ri-save-line align-bottom me-1" />
              )}
              {t("settings.catalog_categories.actions.save")}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default CategoryFormModal;
